import { useTranslation } from 'react-i18next';
import { DetailSection } from '../components/ui/DetailSection.js';
import { Pill } from '../components/PillGroup.js';
import { Button } from '../components/ui/Button.js';
import { LoadingState } from '../components/ui/LoadingState.js';
import { Icon, type IconName } from '../components/Icon.js';
import { OnboardingClaudePanel } from '../components/onboarding/OnboardingClaudePanel.js';
import { ImapSection, CompanyProfileForm } from './SettingsPage.js';
import {
  useOnboardingProgress,
  useComposioConnections,
  useStartComposioConnection,
  type OnboardingStep,
  type OnboardingStepStatus,
  type ConnectableToolkit,
} from '../hooks/useOnboarding.js';

const STEP_ORDER: OnboardingStep[] = ['business_description', 'email', 'connections', 'contacts', 'billing', 'tour'];
const CONNECTABLE: ConnectableToolkit[] = ['instagram', 'facebook', 'googlecalendar'];
const CONNECTION_ICON: Partial<Record<ConnectableToolkit, IconName>> = {
  instagram: 'social-instagram',
  facebook: 'social-facebook',
};

function statusTone(status: OnboardingStepStatus): 'success' | 'muted' | 'warn' {
  if (status === 'completed') return 'success';
  if (status === 'skipped') return 'warn';
  return 'muted';
}

function StepStatusPill({ status }: { status: OnboardingStepStatus }) {
  const { t } = useTranslation();
  return (
    <Pill size="sm" variant="subtle" tone={statusTone(status)}>
      {t(`onboarding.status.${status}`)}
    </Pill>
  );
}

function ConnectionRow({ toolkit }: { toolkit: ConnectableToolkit }) {
  const { t } = useTranslation();
  const { data } = useComposioConnections();
  const startConnection = useStartComposioConnection();
  const status = data?.statuses.find(s => s.toolkit === toolkit);
  const connected = status?.connected ?? false;
  const icon = CONNECTION_ICON[toolkit];

  async function handleConnect() {
    try {
      const link = await startConnection.mutateAsync(toolkit);
      window.open(link.redirectUrl, '_blank', 'noopener,noreferrer');
    } catch {
      // El backend ya le avisó a Eduardo (composioConnections.ts) — acá solo
      // se muestra el estado a través del pill de error, sin bloquear el resto.
    }
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--evo-space-3)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)' }}>
        {icon && <Icon name={icon} size={16} />}
        <span style={{ fontSize: 'var(--evo-list-title-fs)', color: 'var(--evo-fg-1)' }}>
          {t(`onboarding.connections.${toolkit}`)}
        </span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-3)' }}>
        <Pill size="sm" variant="subtle" tone={connected ? 'success' : 'muted'}>
          {connected ? t('onboarding.connections.connected') : t('onboarding.connections.notConnected')}
        </Pill>
        {!connected && (
          <Button
            variant="outline"
            tone="cyan"
            size="sm"
            loading={startConnection.isPending}
            onClick={() => { void handleConnect(); }}
          >
            {t('onboarding.connections.connectBtn')}
          </Button>
        )}
      </div>
      {startConnection.isError && (
        <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-danger)' }}>
          {t('onboarding.connections.connectError')}
        </span>
      )}
    </div>
  );
}

function StepBody({ step }: { step: OnboardingStep }) {
  const { t } = useTranslation();
  if (step === 'email') return <ImapSection />;
  if (step === 'billing') return <CompanyProfileForm />;
  if (step === 'connections') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-4)' }}>
        <p style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-3)', margin: 0, lineHeight: 1.6 }}>
          {t('onboarding.step.connections.description')}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-3)' }}>
          {CONNECTABLE.map(toolkit => <ConnectionRow key={toolkit} toolkit={toolkit} />)}
        </div>
      </div>
    );
  }
  return (
    <p style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-3)', margin: 0, lineHeight: 1.6 }}>
      {t(`onboarding.step.${step}.description`)}
    </p>
  );
}

export function OnboardingPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useOnboardingProgress();
  const progress = data?.progress ?? [];
  const byStep = new Map(progress.map(s => [s.step, s]));
  const done = progress.filter(s => s.status === 'completed' || s.status === 'skipped').length;

  return (
    <div style={{ height: '100%', display: 'flex', overflow: 'hidden' }}>
      <div
        className="animate-fade-blur-in"
        style={{
          flex: 1,
          minWidth: 0,
          overflowY: 'auto',
          padding: 'var(--evo-space-6) var(--evo-content-pad-x)',
        }}
      >
        <div style={{ maxWidth: '760px' }}>
          <h1 style={{
            fontSize: 'var(--evo-fs-h1)', color: 'var(--evo-fg-1)', margin: 0,
            fontFamily: 'var(--font-display)',
          }}>
            {t('onboarding.pageTitle')}
          </h1>
          <p style={{ fontSize: 'var(--evo-fs-body-lg)', color: 'var(--evo-fg-3)', marginTop: 'var(--evo-space-2)', lineHeight: 1.6 }}>
            {t('onboarding.pageSubtitle')}
          </p>
          {progress.length > 0 && (
            <div style={{ marginTop: 'var(--evo-space-3)' }}>
              <Pill size="sm" variant="subtle" tone="cyan">
                {t('onboarding.progressLabel', { done, total: progress.length })}
              </Pill>
            </div>
          )}

          {isLoading && <LoadingState />}

          {!isLoading && STEP_ORDER.map((step, idx) => {
            const state = byStep.get(step);
            return (
              <DetailSection
                key={step}
                title={t(`onboarding.step.${step}.title`)}
                titleRight={<StepStatusPill status={state?.status ?? 'pending'} />}
                divider={idx > 0}
              >
                <StepBody step={step} />
              </DetailSection>
            );
          })}
        </div>
      </div>

      <OnboardingClaudePanel />
    </div>
  );
}
